import React from 'react';

import { Collapsible, CollapsibleHeaderContent, CollapsibleHeading } from '@dndbeyond/character-components/es';
import {
    BaseSpellContract,
    CharacterTheme,
    CharClass,
    ClassUtils,
    DataOriginRefData,
    EntityValueLookup,
    OverallSpellInfo,
    RuleData,
    Spell,
    SpellCasterInfo,
} from '@dndbeyond/character-rules-engine/es';

import { ApiSpellsRequest } from '../../selectors/composite/apiCreator';
import SpellManager from '../SpellManager';

interface Props {
    loadRemainingSpellList: ApiSpellsRequest | null;
    loadAlwaysKnownSpells?: ApiSpellsRequest | null;
    charClass: CharClass;
    activeSpells: Array<Spell>;
    spells: Array<Spell>;
    isPrepareMaxed: boolean;
    isCantripsKnownMaxed: boolean;
    isSpellsKnownMaxed: boolean;
    knownSpellIds: Array<string>;
    spellcastingModifier: number;
    classHeader: string;
    hasMultipleSpellClasses: boolean;
    buttonAddText: string;
    buttonRemoveText: string;
    activeFeatureCantripCount: number;
    knownFeatureSpellCount: number;
    knownSpellCount: number;
    activeCantripsCount: number;
    preparedSpellCount: number;
    knownCantripsMax: number | null;
    knownSpellsMax: number | null;
    prepareMax: number | null;
    onAlwaysKnownLoad: (spells: Array<BaseSpellContract>, classId: number) => void;
    onSpellPrepare: (spell: Spell, classMappingId: number) => void;
    onSpellUnprepare: (spell: Spell, classMappingId: number) => void;
    onSpellRemove: (spell: Spell, classMappingId: number) => void;
    onSpellAdd: (spell: Spell, classMappingId: number) => void;
    onDisplayAsAttack?: (spell: Spell, classMappingId: number) => void;
    onSpellDetailShow?: (spell: Spell, classMappingId: number) => void;
    overallSpellInfo: OverallSpellInfo;
    spellCasterInfo: SpellCasterInfo;
    ruleData: RuleData;
    entityValueLookup: EntityValueLookup;
    dataOriginRefData: DataOriginRefData;
    proficiencyBonus: number;
    theme: CharacterTheme;
}
interface State {
    collapsedGroups: {
        activeSpells: boolean;
        spellbook: boolean;
        addSpells: boolean;
    };
}
export default class ClassSpellManager extends React.PureComponent<Props, State> {
    static defaultProps = {
        classHeader: '',
        buttonAddText: 'Learn',
        buttonRemoveText: 'Remove',
        hasMultipleSpellClasses: false,
    };

    constructor(props: Props) {
        super(props);

        const { charClass, hasMultipleSpellClasses } = props;

        const isSpellbookSpellcaster = ClassUtils.isSpellbookSpellcaster(charClass);
        const isSpellbookEmpty = this.isSpellbookEmpty(props);

        this.state = {
            collapsedGroups: {
                activeSpells: hasMultipleSpellClasses || (isSpellbookSpellcaster && isSpellbookEmpty),
                spellbook:
                    hasMultipleSpellClasses || !isSpellbookSpellcaster || (isSpellbookSpellcaster && !isSpellbookEmpty),
                addSpells: true,
            },
        };
    }

    handlePrepare = (spell: Spell): void => {
        const { onSpellPrepare, charClass } = this.props;

        if (onSpellPrepare) {
            onSpellPrepare(spell, ClassUtils.getMappingId(charClass));
        }
    };

    handleUnprepare = (spell: Spell): void => {
        const { onSpellUnprepare, charClass } = this.props;

        if (onSpellUnprepare) {
            onSpellUnprepare(spell, ClassUtils.getMappingId(charClass));
        }
    };

    handleRemove = (spell: Spell): void => {
        const { onSpellRemove, charClass } = this.props;

        if (onSpellRemove) {
            onSpellRemove(spell, ClassUtils.getMappingId(charClass));
        }
    };

    handleAdd = (spell: Spell): void => {
        const { onSpellAdd, charClass } = this.props;

        if (onSpellAdd) {
            onSpellAdd(spell, ClassUtils.getMappingId(charClass));
        }
    };

    handleAlwaysKnownLoad = (spells: Array<BaseSpellContract>): void => {
        const { onAlwaysKnownLoad, charClass } = this.props;

        if (onAlwaysKnownLoad) {
            onAlwaysKnownLoad(spells, ClassUtils.getActiveId(charClass));
        }
    };

    handleShowSpellGroup = (key: keyof State['collapsedGroups']): void => {
        this.setState((prevState: State): State => {
            let resetState = {
                activeSpells: true,
                addSpells: true,
                spellbook: true,
            };

            return {
                ...prevState,
                collapsedGroups: {
                    ...resetState,
                    [key]: !prevState.collapsedGroups[key],
                },
            };
        });
    };

    isSpellbookEmpty = (props: Props): boolean => {
        const { knownSpellCount, activeCantripsCount } = props;

        return knownSpellCount === 0 && activeCantripsCount === 0;
    };

    doesAvailableSpellsHaveNotifications = (): boolean => {
        const {
            prepareMax,
            knownCantripsMax,
            knownSpellsMax,
            knownSpellCount,
            activeCantripsCount,
            preparedSpellCount,
        } = this.props;

        if (knownCantripsMax !== null && activeCantripsCount > knownCantripsMax) {
            return true;
        }

        if (prepareMax && preparedSpellCount > prepareMax) {
            return true;
        }

        if (knownSpellsMax && knownSpellCount > knownSpellsMax) {
            return true;
        }

        return false;
    };

    renderSpellListSpellStatus = (): React.ReactNode => {
        const {
            prepareMax,
            knownCantripsMax,
            knownSpellsMax,
            knownSpellCount,
            activeCantripsCount,
            preparedSpellCount,
            activeFeatureCantripCount,
            knownFeatureSpellCount,
        } = this.props;

        let featureSpellCount: number = activeFeatureCantripCount + knownFeatureSpellCount;
        let calloutNode: React.ReactNode;
        if (featureSpellCount > 0) {
            calloutNode = '*';
        }

        let cantripsNode: React.ReactNode = '';
        let cantripsClsNames: Array<string> = [
            'ct-class-spell-manager__info-entry',
            'ct-class-spell-manager__info-entry--cantrips',
        ];
        if (knownCantripsMax !== null) {
            if (activeCantripsCount === knownCantripsMax) {
                cantripsNode = `Cantrips: ${knownCantripsMax}`;
            } else {
                cantripsNode = `Cantrips: ${activeCantripsCount}/${knownCantripsMax}`;

                if (activeCantripsCount > knownCantripsMax) {
                    cantripsClsNames.push('ct-class-spell-manager__info-entry--exceeded');
                }
            }
        }

        let spellsNode: React.ReactNode;
        let spellsExtraNode: React.ReactNode;
        let spellsClsNames: Array<string> = [
            'ct-class-spell-manager__info-entry',
            'ct-class-spell-manager__info-entry--spells',
        ];
        if (prepareMax) {
            if (preparedSpellCount === prepareMax) {
                spellsNode = `Prepared Spells: ${preparedSpellCount}`;
            } else {
                spellsNode = `Prepared Spells: ${preparedSpellCount}/${prepareMax}`;

                if (preparedSpellCount > prepareMax) {
                    spellsClsNames.push('ct-class-spell-manager__info-entry--exceeded');
                }
            }

            spellsExtraNode = (
                <span className="ct-class-spell-manager__info-entry-extra">({knownSpellCount} Known)</span>
            );
        } else if (knownSpellsMax) {
            if (knownSpellCount === knownSpellsMax) {
                spellsNode = `Known Spells: ${knownSpellCount}`;
            } else {
                spellsNode = `Known Spells: ${knownSpellCount}/${knownSpellsMax}`;

                if (knownSpellCount > knownSpellsMax) {
                    spellsClsNames.push('ct-class-spell-manager__info-entry--exceeded');
                }
            }
        }

        return (
            <div className="ct-class-spell-manager__info">
                <div className={cantripsClsNames.join(' ')}>
                    {cantripsNode}
                    {calloutNode}
                </div>
                <div className={spellsClsNames.join(' ')}>
                    {spellsNode}
                    {spellsExtraNode}
                    {calloutNode}
                </div>
                {featureSpellCount > 0 && (
                    <div className="ct-class-spell-manager__info-features">
                        *{featureSpellCount} spell
                        {featureSpellCount !== 1 ? 's' : ''} included from class features.
                    </div>
                )}
            </div>
        );
    };

    renderEmptyActiveSpells = (): React.ReactNode => {
        const { charClass } = this.props;

        return (
            <div className="ct-class-spell-manager__empty">
                {ClassUtils.isSpellbookSpellcaster(charClass) && (
                    <React.Fragment>You currently have no prepared spells.</React.Fragment>
                )}
                {ClassUtils.isPreparedSpellcaster(charClass) && (
                    <React.Fragment>
                        You currently have no prepared spells. Learn and prepare spells from your list of available
                        spells below.
                    </React.Fragment>
                )}
                {ClassUtils.isKnownSpellcaster(charClass) && (
                    <React.Fragment>
                        You currently have no known spells. Learn spells from your list of available spells below.
                    </React.Fragment>
                )}
            </div>
        );
    };

    renderActiveSpells = (): React.ReactNode => {
        const {
            activeSpells,
            charClass,
            isPrepareMaxed,
            isCantripsKnownMaxed,
            isSpellsKnownMaxed,
            knownSpellIds,
            buttonAddText,
            spellCasterInfo,
            ruleData,
            entityValueLookup,
            overallSpellInfo,
            dataOriginRefData,
            proficiencyBonus,
            theme,
        } = this.props;

        return (
            <div className="ct-class-spell-manager__active">
                <SpellManager
                    theme={theme}
                    isPrepareMaxed={isPrepareMaxed}
                    isCantripsKnownMaxed={isCantripsKnownMaxed}
                    isSpellsKnownMaxed={isSpellsKnownMaxed}
                    spells={activeSpells}
                    enableSpellRemove={!ClassUtils.isSpellbookSpellcaster(charClass)}
                    enablePrepare={false}
                    enableUnprepare={true}
                    showFilters={false}
                    knownSpellIds={knownSpellIds}
                    onPrepare={this.handlePrepare}
                    onUnprepare={this.handleUnprepare}
                    onRemove={this.handleRemove}
                    onAdd={this.handleAdd}
                    addButtonText={buttonAddText}
                    spellCasterInfo={spellCasterInfo}
                    ruleData={ruleData}
                    buttonActiveStyle="outline"
                    buttonUnprepareText="Unprepare"
                    entityValueLookup={entityValueLookup}
                    overallSpellInfo={overallSpellInfo}
                    dataOriginRefData={dataOriginRefData}
                    showCustomize={false}
                    proficiencyBonus={proficiencyBonus}
                />
            </div>
        );
    };

    renderActiveSpellsGroup = (): React.ReactNode => {
        const { collapsedGroups } = this.state;
        const { charClass, activeSpells } = this.props;

        let heading: React.ReactNode = 'Prepared Spells';
        if (ClassUtils.isKnownSpellcaster(charClass)) {
            heading = 'Known Spells';
        }

        let headingNode: React.ReactNode = (
            <CollapsibleHeading>
                {heading}
                <span className="ct-class-spell-manager__heading-extra">({activeSpells.length})</span>
            </CollapsibleHeading>
        );

        let headerNode: React.ReactNode = <CollapsibleHeaderContent heading={headingNode} />;

        return (
            <Collapsible
                collapsed={collapsedGroups.activeSpells}
                header={headerNode}
                onChangeHandler={this.handleShowSpellGroup.bind(this, 'activeSpells')}
                className="ct-class-spell-manager__group"
            >
                {activeSpells.length > 0 ? this.renderActiveSpells() : this.renderEmptyActiveSpells()}
            </Collapsible>
        );
    };

    renderSpellbook = (): React.ReactNode => {
        const { collapsedGroups } = this.state;
        const {
            charClass,
            spells,
            isPrepareMaxed,
            isCantripsKnownMaxed,
            isSpellsKnownMaxed,
            knownSpellIds,
            buttonAddText,
            spellCasterInfo,
            ruleData,
            entityValueLookup,
            overallSpellInfo,
            dataOriginRefData,
            proficiencyBonus,
            theme,
        } = this.props;

        if (!ClassUtils.isSpellbookSpellcaster(charClass)) {
            return null;
        }

        const isSpellbookEmpty = this.isSpellbookEmpty(this.props);

        let headerNode: React.ReactNode = <CollapsibleHeaderContent heading="Spellbook" />;

        return (
            <Collapsible
                collapsed={collapsedGroups.spellbook}
                header={headerNode}
                onChangeHandler={this.handleShowSpellGroup.bind(this, 'spellbook')}
                className="ct-class-spell-manager__group"
            >
                {isSpellbookEmpty && (
                    <div className="ct-class-spell-manager__empty">
                        You currently have no known spells. Learn spells from your list of available spells below.
                    </div>
                )}
                {!isSpellbookEmpty && (
                    <React.Fragment>
                        {this.renderSpellListSpellStatus()}
                        <SpellManager
                            theme={theme}
                            isPrepareMaxed={isPrepareMaxed}
                            isCantripsKnownMaxed={isCantripsKnownMaxed}
                            isSpellsKnownMaxed={isSpellsKnownMaxed}
                            enableAdd={false}
                            enableSpellRemove={false}
                            spells={spells}
                            knownSpellIds={knownSpellIds}
                            onPrepare={this.handlePrepare}
                            onUnprepare={this.handleUnprepare}
                            onRemove={this.handleRemove}
                            onAdd={this.handleAdd}
                            addButtonText={buttonAddText}
                            spellCasterInfo={spellCasterInfo}
                            ruleData={ruleData}
                            entityValueLookup={entityValueLookup}
                            overallSpellInfo={overallSpellInfo}
                            dataOriginRefData={dataOriginRefData}
                            showCustomize={false}
                            proficiencyBonus={proficiencyBonus}
                        />
                    </React.Fragment>
                )}
            </Collapsible>
        );
    };

    renderAddSpells = (): React.ReactNode => {
        const { collapsedGroups } = this.state;
        const {
            charClass,
            loadRemainingSpellList,
            loadAlwaysKnownSpells,
            spells,
            isPrepareMaxed,
            isCantripsKnownMaxed,
            isSpellsKnownMaxed,
            knownSpellIds,
            buttonAddText,
            spellCasterInfo,
            ruleData,
            entityValueLookup,
            overallSpellInfo,
            dataOriginRefData,
            proficiencyBonus,
            theme,
        } = this.props;

        let heading: React.ReactNode = 'Add Spells';
        if (ClassUtils.isPreparedSpellcaster(charClass)) {
            heading = 'Known Spells';
        }

        let headingNode: React.ReactNode = (
            <CollapsibleHeading>
                {heading}
                {this.doesAvailableSpellsHaveNotifications() && (
                    <div className="ct-class-spell-manager__notification">!</div>
                )}
            </CollapsibleHeading>
        );

        let headerNode: React.ReactNode = <CollapsibleHeaderContent heading={headingNode} />;

        return (
            <Collapsible
                collapsed={collapsedGroups.addSpells}
                header={headerNode}
                onChangeHandler={this.handleShowSpellGroup.bind(this, 'addSpells')}
                className="ct-class-spell-manager__group"
            >
                {this.renderSpellListSpellStatus()}
                <SpellManager
                    theme={theme}
                    isPrepareMaxed={isPrepareMaxed}
                    isCantripsKnownMaxed={isCantripsKnownMaxed}
                    isSpellsKnownMaxed={isSpellsKnownMaxed}
                    loadSpells={loadRemainingSpellList}
                    loadAlwaysKnownSpells={loadAlwaysKnownSpells}
                    spells={spells}
                    enablePrepare={!ClassUtils.isSpellbookSpellcaster(charClass)}
                    enableUnprepare={!ClassUtils.isSpellbookSpellcaster(charClass)}
                    knownSpellIds={knownSpellIds}
                    onAlwaysKnownLoad={this.handleAlwaysKnownLoad}
                    onPrepare={this.handlePrepare}
                    onUnprepare={this.handleUnprepare}
                    onRemove={this.handleRemove}
                    onAdd={this.handleAdd}
                    addButtonText={buttonAddText}
                    spellCasterInfo={spellCasterInfo}
                    ruleData={ruleData}
                    entityValueLookup={entityValueLookup}
                    overallSpellInfo={overallSpellInfo}
                    dataOriginRefData={dataOriginRefData}
                    showExpandedType={true}
                    showCustomize={false}
                    proficiencyBonus={proficiencyBonus}
                />
            </Collapsible>
        );
    };

    render() {
        const { charClass } = this.props;

        return (
            <div className="ct-class-spell-manager">
                <div className="ct-class-spell-manager__header">
                    <div className="ct-class-spell-manager__portrait">
                        <img
                            className="ct-class-spell-manager__portrait-img"
                            src={ClassUtils.getPortraitUrl(charClass)}
                            alt=""
                        />
                    </div>
                    <div className="ct-class-spell-manager__heading">{ClassUtils.getName(charClass)}</div>
                </div>
                {this.renderAddSpells()}
                {this.renderActiveSpellsGroup()}
                {this.renderSpellbook()}
            </div>
        );
    }
}
